import { useDispatch } from "react-redux";
import { updateBackgroundInfo } from "../../../../redux/slices/userProfileSlice";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import Logo from "../../../../components/Logo/Logo";
import { getReligions } from "../../../../constants/adminData";
import { useAppSelector } from "../../../../store";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";

import { religiousAssociation } from "@/types/admin";

interface ReligiousAssociationProps {
  onBack: () => void;
  onContinue: () => void;
}

export default function ReligiousAssociation({
  onBack,
  onContinue,
}: ReligiousAssociationProps) {
  const dispatch = useDispatch();
  const profile = useAppSelector((state) => state.userProfile);

  const [religionId, setReligionId] = useState("");
  const [religions, setReligions] = useState<religiousAssociation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && religions.length > 0) {
      const saved = profile.backgroundInfo.religionId;
      setReligionId(saved ? String(saved) : "");
    }
  }, [loading, religions, profile.backgroundInfo.religionId]);

  useEffect(() => {
    const loadReligions = async () => {
      try {
        const response = await getReligions();
        const safeData = Array.isArray(response?.data) ? response.data : [];
        setReligions(safeData);
      } catch {
        setError("Failed to load religious associations");
      } finally {
        setLoading(false);
      }
    };

    loadReligions();
  }, []);

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!religionId) {
      alert("Please select your religious association");
      return;
    }

    await dispatch(
      updateBackgroundInfo({
        religionId: Number(religionId),
      })
    );

    onContinue();
  };

  return (
    <div className="h-screen md:h-auto bg-[#F5F5F5] flex flex-col items-center px-6 pt-4 pb-12 relative">
      <div className="w-full max-w-xl flex flex-col items-center flex-1">
        <button
          onClick={onBack}
          className="absolute left-6 top-4 p-2 min-w-[44px] min-h-[44px]"
          aria-label="Go back"
        >
          <ArrowLeft className="w-6 h-6 text-brand-primary" />
        </button>

        <div className="flex justify-center mb-0">
          <Logo />
        </div>

        <h1 className="text-[26px] md:text-3xl lg:text-4xl font-semibold text-center mb-8">
          Religious Association
        </h1>

        <form onSubmit={handleContinue} className="w-full flex flex-col flex-1">
          <div
            className={`rounded-lg mb-auto ${
              religionId
                ? "bg-gradient-to-r from-brand-primary to-brand-gray p-[2px]"
                : ""
            }`}
          >
            <Select value={religionId} onValueChange={setReligionId}>
              <SelectTrigger
                className={`w-full bg-white h-12 md:h-14 px-4 text-[#2A3F5F] ${
                  religionId
                    ? "border-0 rounded-[6px]"
                    : "border-2 border-gray-300 rounded-lg"
                }`}
              >
                <SelectValue placeholder="Select Religion" />
              </SelectTrigger>

              <SelectContent>
                <div className="max-h-[220px] overflow-y-auto custom-scroll">
                  {loading && (
                    <div className="px-4 py-2 text-gray-500">
                      Loading...
                    </div>
                  )}

                  {error && (
                    <div className="px-4 py-2 text-red-500">
                      {error}
                    </div>
                  )}

                  {!loading &&
                    !error &&
                    religions
                      .filter(r => r.religionName !== "Does Not Matter")
                      .map(rel => (
                        <SelectItem
                          key={rel.religionId}
                          value={String(rel.religionId)}
                        >
                          {rel.religionName}
                        </SelectItem>
                      ))}
                </div>
              </SelectContent>
            </Select>
          </div>

          <div className="mt-auto pt-10 pb-4 w-full">
            <button
              type="submit"
              disabled={!religionId || loading}
              className="w-full bg-brand-primary text-white h-12 md:h-14 lg:h-16 text-lg md:text-xl lg:text-2xl font-medium rounded-lg hover:bg-brand-hover transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
